import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Plus, DollarSign, Search, Pencil, Trash2, AlertTriangle, Variable, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import FormulaSimulator from './product/FormulaSimulator';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const EMPTY_FORM = {
  product_id: '',
  description: '',
  unit_of_measure_id: '',
  min_qty: 1,
  max_qty: '',
  price_type: 'fixed',
  unit_price: '',
  unit_price_formula: '',
  quantity_formula: '',
  total_formula: '',
};

const PricesPage = () => {
  const [prices, setPrices] = useState([]);
  const [products, setProducts] = useState([]);
  const [units, setUnits] = useState([]);
  const [variables, setVariables] = useState([]);
  const [filterProduct, setFilterProduct] = useState('');
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPrice, setEditingPrice] = useState(null);
  const [formData, setFormData] = useState({ ...EMPTY_FORM });
  const [submitting, setSubmitting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [showVarPanel, setShowVarPanel] = useState(false);
  const [varSearch, setVarSearch] = useState('');

  const loadAll = useCallback(async () => {
    try {
      const [pricesRes, productsRes, unitsRes, varsRes] = await Promise.all([
        axios.get(`${API_URL}/prices${filterProduct ? `?product_id=${filterProduct}` : ''}`, { withCredentials: true }),
        axios.get(`${API_URL}/products`, { withCredentials: true }),
        axios.get(`${API_URL}/units-of-measure`, { withCredentials: true }),
        axios.get(`${API_URL}/variables`, { withCredentials: true }),
      ]);
      setPrices(pricesRes.data);
      setProducts(productsRes.data);
      setUnits(unitsRes.data);
      setVariables(varsRes.data);
    } catch (error) {
      console.error('Failed to load:', error);
    } finally {
      setLoading(false);
    }
  }, [filterProduct]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const openCreate = () => {
    setEditingPrice(null);
    setFormData({ ...EMPTY_FORM });
    setDialogOpen(true);
  };

  const openEdit = (price) => {
    setEditingPrice(price);
    setFormData({
      product_id: price.product_id,
      description: price.description || '',
      unit_of_measure_id: price.unit_of_measure_id || '',
      min_qty: price.min_qty || 1,
      max_qty: price.max_qty || '',
      price_type: price.price_type || 'fixed',
      unit_price: price.unit_price ?? '',
      unit_price_formula: price.unit_price_formula || '',
      quantity_formula: price.quantity_formula || '',
      total_formula: price.total_formula || '',
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    const payload = {
      ...formData,
      unit_price: formData.price_type === 'fixed' ? (parseFloat(formData.unit_price) || 0) : null,
      min_qty: parseInt(formData.min_qty) || 1,
      max_qty: formData.max_qty ? parseInt(formData.max_qty) : null,
      unit_of_measure_id: formData.unit_of_measure_id || null,
      unit_price_formula: formData.price_type === 'formula' ? formData.unit_price_formula : null,
    };
    try {
      if (editingPrice) {
        await axios.put(`${API_URL}/prices/${editingPrice.price_id}`, payload, { withCredentials: true });
        toast.success('Preco atualizado');
      } else {
        await axios.post(`${API_URL}/prices`, payload, { withCredentials: true });
        toast.success('Preco cadastrado');
      }
      setDialogOpen(false);
      loadAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (priceId) => {
    try {
      await axios.delete(`${API_URL}/prices/${priceId}`, { withCredentials: true });
      toast.success('Preco excluido');
      setDeleteTarget(null);
      loadAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir');
    }
  };

  const insertVariable = (varName) => {
    // Insert variable name into the currently focused formula field
    const active = document.activeElement;
    if (active && active.dataset.formulaField) {
      const field = active.dataset.formulaField;
      const start = active.selectionStart || 0;
      const current = formData[field] || '';
      const newVal = current.slice(0, start) + varName + current.slice(start);
      setFormData({ ...formData, [field]: newVal });
      setTimeout(() => {
        active.focus();
        active.setSelectionRange(start + varName.length, start + varName.length);
      }, 50);
    } else {
      // Fallback: append to unit_price_formula
      const field = formData.price_type === 'formula' ? 'unit_price_formula' : 'quantity_formula';
      setFormData({ ...formData, [field]: (formData[field] || '') + varName });
    }
  };

  // Build simulator-compatible formData for FormulaSimulator
  const simulatorFormData = {
    unit_price_type: formData.price_type,
    unit_price_fixed: formData.unit_price,
    unit_price_formula: formData.unit_price_formula,
    quantity_type: formData.quantity_formula ? 'formula' : 'manual',
    quantity_formula: formData.quantity_formula,
    total_formula: formData.total_formula,
  };

  const filteredVars = variables.filter(v =>
    !varSearch || v.name.includes(varSearch.toUpperCase()) || v.label.toLowerCase().includes(varSearch.toLowerCase())
  );

  // Group variables by category
  const groupedVars = {};
  filteredVars.forEach(v => {
    const cat = v.category || 'geral';
    if (!groupedVars[cat]) groupedVars[cat] = [];
    groupedVars[cat].push(v);
  });

  if (loading) {
    return <div className="flex justify-center p-12"><div className="w-12 h-12 border-4 border-[#AF2628] border-t-transparent rounded-full animate-spin"></div></div>;
  }

  return (
    <div className="space-y-6" data-testid="prices-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-[#111111]">Tabela de Precos</h1>
          <p className="text-[#6E6E73]">Regras de precificacao por produto (fixo, formula ou faixa)</p>
        </div>
        <Button onClick={openCreate} className="bg-[#AF2628] hover:bg-[#961B22]" data-testid="new-price-button">
          <Plus className="w-4 h-4 mr-2" />Novo Preco
        </Button>
      </div>

      <div className="bg-white border border-[#E5E5EA] rounded-xl p-4">
        <div className="flex items-center gap-3 mb-4">
          <Select value={filterProduct || 'all'} onValueChange={(val) => setFilterProduct(val === 'all' ? '' : val)}>
            <SelectTrigger className="w-64"><SelectValue placeholder="Filtrar por produto" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os produtos</SelectItem>
              {products.map(p => <SelectItem key={p.product_id} value={p.product_id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {prices.length > 0 ? (
          <div className="border border-[#E5E5EA] rounded-lg overflow-hidden">
            <table className="w-full">
              <thead className="bg-[#F7F7F8]">
                <tr>
                  <th className="text-left p-3 text-xs font-medium text-[#6E6E73]">PRODUTO</th>
                  <th className="text-left p-3 text-xs font-medium text-[#6E6E73]">DESCRICAO</th>
                  <th className="text-left p-3 text-xs font-medium text-[#6E6E73]">TIPO</th>
                  <th className="text-center p-3 text-xs font-medium text-[#6E6E73]">FAIXA (QTD)</th>
                  <th className="text-right p-3 text-xs font-medium text-[#6E6E73]">PRECO / FORMULA</th>
                  <th className="text-right p-3 text-xs font-medium text-[#6E6E73]">ACOES</th>
                </tr>
              </thead>
              <tbody>
                {prices.map((price) => (
                  <tr key={price.price_id} className="border-t border-[#E5E5EA] hover:bg-[#F7F7F8]" data-testid={`price-row-${price.price_id}`}>
                    <td className="p-3 text-sm font-medium">{price.product_name}</td>
                    <td className="p-3 text-sm text-[#6E6E73]">{price.description || '-'}</td>
                    <td className="p-3">
                      {(price.price_type === 'formula') ? (
                        <span className="text-xs bg-purple-50 px-2 py-1 rounded text-purple-600 font-medium">Formula</span>
                      ) : (
                        <span className="text-xs bg-blue-50 px-2 py-1 rounded text-blue-600 font-medium">Fixo</span>
                      )}
                    </td>
                    <td className="p-3 text-sm text-center">
                      {price.min_qty}{price.max_qty ? ` - ${price.max_qty}` : '+'}
                    </td>
                    <td className="p-3 text-sm text-right">
                      {(price.price_type === 'formula') ? (
                        <span className="font-mono text-xs text-purple-600 truncate block max-w-48" title={price.unit_price_formula}>{price.unit_price_formula}</span>
                      ) : (
                        <span className="font-medium text-[#AF2628]">R$ {(price.unit_price || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
                      )}
                    </td>
                    <td className="p-3 text-right">
                      <div className="flex gap-1 justify-end">
                        <Button size="sm" variant="ghost" onClick={() => openEdit(price)}><Pencil className="w-3.5 h-3.5" /></Button>
                        <Button size="sm" variant="ghost" onClick={() => setDeleteTarget(price)} className="text-red-500"><Trash2 className="w-3.5 h-3.5" /></Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-12">
            <DollarSign className="w-12 h-12 text-[#AEAEB2] mx-auto mb-3" />
            <p className="text-[#6E6E73] mb-4">Nenhum preco cadastrado</p>
            <Button onClick={openCreate} className="bg-[#AF2628] hover:bg-[#961B22]"><Plus className="w-4 h-4 mr-2" />Cadastrar Primeiro Preco</Button>
          </div>
        )}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) { setEditingPrice(null); setShowVarPanel(false); } }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editingPrice ? 'Editar Regra de Preco' : 'Nova Regra de Preco'}</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="price-form">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Produto *</Label>
                <Select value={formData.product_id} onValueChange={(val) => setFormData({ ...formData, product_id: val })}>
                  <SelectTrigger><SelectValue placeholder="Selecione um produto" /></SelectTrigger>
                  <SelectContent>
                    {products.map(p => <SelectItem key={p.product_id} value={p.product_id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Unidade de Medida</Label>
                <Select value={formData.unit_of_measure_id || 'none'} onValueChange={(val) => setFormData({ ...formData, unit_of_measure_id: val === 'none' ? '' : val })}>
                  <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Nenhuma</SelectItem>
                    {units.map(u => <SelectItem key={u.unit_id || u.uom_id} value={u.unit_id || u.uom_id}>{u.name} ({u.abbreviation})</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Descricao</Label>
              <Input value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} placeholder="Ex: Licenca anual - Faixa 1" />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Qtd Minima</Label>
                <Input type="number" min="1" value={formData.min_qty} onChange={(e) => setFormData({ ...formData, min_qty: e.target.value })} />
              </div>
              <div>
                <Label>Qtd Maxima</Label>
                <Input type="number" min="1" value={formData.max_qty} onChange={(e) => setFormData({ ...formData, max_qty: e.target.value })} placeholder="Ilimitado" />
              </div>
            </div>

            {/* Pricing Rule Section */}
            <div className="border border-[#E5E5EA] rounded-lg p-4 bg-[#FAFAFA]">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-semibold text-[#111111]">Regra de Calculo</h4>
                <Button type="button" variant="ghost" size="sm" className="h-7 text-xs text-[#AF2628]" onClick={() => setShowVarPanel(!showVarPanel)} data-testid="toggle-var-panel">
                  <Variable className="w-3.5 h-3.5 mr-1" />
                  {showVarPanel ? 'Fechar Variaveis' : 'Ver Variaveis'}
                  {showVarPanel ? <ChevronUp className="w-3 h-3 ml-1" /> : <ChevronDown className="w-3 h-3 ml-1" />}
                </Button>
              </div>

              {/* Variable Panel */}
              {showVarPanel && (
                <div className="mb-4 border border-[#AF2628]/20 rounded-lg p-3 bg-white" data-testid="variable-panel">
                  <Input
                    value={varSearch}
                    onChange={(e) => setVarSearch(e.target.value)}
                    placeholder="Buscar variavel..."
                    className="mb-2 h-7 text-xs"
                  />
                  <div className="max-h-40 overflow-y-auto space-y-2">
                    {Object.entries(groupedVars).map(([cat, vars]) => (
                      <div key={cat}>
                        <p className="text-[9px] uppercase text-[#AEAEB2] font-bold tracking-wide">{cat}</p>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {vars.map(v => (
                            <button
                              key={v.var_id}
                              type="button"
                              onClick={() => insertVariable(v.name)}
                              className="text-[10px] px-2 py-1 rounded bg-[#F7F7F8] text-[#111111] hover:bg-[#AF2628] hover:text-white transition-colors border border-[#E5E5EA] cursor-pointer"
                              title={v.label + (v.description ? ` - ${v.description}` : '')}
                              data-testid={`var-btn-${v.name}`}
                            >
                              {v.name} <span className="text-[#AEAEB2] ml-1">{v.label}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                    {Object.keys(groupedVars).length === 0 && (
                      <p className="text-xs text-[#AEAEB2]">Nenhuma variavel encontrada</p>
                    )}
                  </div>
                </div>
              )}

              <div>
                <Label className="text-xs">Tipo do Valor Unitario</Label>
                <Select value={formData.price_type} onValueChange={(val) => setFormData({ ...formData, price_type: val })}>
                  <SelectTrigger className="mt-1" data-testid="price-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fixed">Fixo (R$)</SelectItem>
                    <SelectItem value="formula">Formula</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {formData.price_type === 'fixed' && (
                <div className="mt-3">
                  <Label className="text-xs">Valor Unitario Fixo (R$)</Label>
                  <Input
                    type="number" step="0.01" min="0"
                    value={formData.unit_price}
                    onChange={(e) => setFormData({ ...formData, unit_price: e.target.value })}
                    placeholder="Ex: 42000.00"
                    className="mt-1"
                    data-testid="price-value-input"
                  />
                </div>
              )}

              {formData.price_type === 'formula' && (
                <div className="mt-3">
                  <Label className="text-xs">Formula do Valor Unitario</Label>
                  <Input
                    value={formData.unit_price_formula}
                    onChange={(e) => setFormData({ ...formData, unit_price_formula: e.target.value })}
                    placeholder="Ex: 150000 + 0.05 * QTD_ITENS + 150 * QTD_UGS"
                    className="mt-1 font-mono text-sm"
                    data-formula-field="unit_price_formula"
                    data-testid="unit-price-formula-input"
                  />
                  <p className="text-[10px] text-[#AEAEB2] mt-1">Clique em "Ver Variaveis" para consultar e inserir variaveis</p>
                </div>
              )}

              <div className="mt-3">
                <Label className="text-xs">Formula da Quantidade (opcional - se vazio, quantidade e informada no documento)</Label>
                <Input
                  value={formData.quantity_formula}
                  onChange={(e) => setFormData({ ...formData, quantity_formula: e.target.value })}
                  placeholder="Ex: PESSOAS * HORAS_DIA * DIAS_UTEIS"
                  className="mt-1 font-mono text-sm"
                  data-formula-field="quantity_formula"
                  data-testid="quantity-formula-input"
                />
              </div>

              <div className="mt-3">
                <Label className="text-xs">Formula do Valor Total (opcional - padrao: Quantidade x Valor Unitario)</Label>
                <Input
                  value={formData.total_formula}
                  onChange={(e) => setFormData({ ...formData, total_formula: e.target.value })}
                  placeholder="Ex: QUANTIDADE * VALOR_UNITARIO"
                  className="mt-1 font-mono text-sm"
                  data-formula-field="total_formula"
                  data-testid="total-formula-input"
                />
              </div>

              <div className="mt-4">
                <FormulaSimulator formData={simulatorFormData} />
              </div>
            </div>

            <div className="flex gap-2 justify-end">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
              <Button type="submit" className="bg-[#AF2628] hover:bg-[#961B22]" disabled={submitting} data-testid="price-submit-button">
                {submitting ? 'Salvando...' : (editingPrice ? 'Salvar' : 'Cadastrar')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-red-600" />Excluir Preco</DialogTitle>
            <DialogDescription>Excluir este preco de <strong>{deleteTarget?.product_name}</strong>?</DialogDescription>
          </DialogHeader>
          <div className="flex gap-2 justify-end pt-4">
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancelar</Button>
            <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={() => handleDelete(deleteTarget?.price_id)}>Excluir</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PricesPage;
